import { Button, CircularProgress, Typography } from "@mui/material"
import AuthLayout from "../../components/auth-layout"
import { CenteredBox, PageLoader, Stack } from "src/shared/components"
import OutlinedInput from "src/shared/components/outlined-input"
import * as yup from "yup"
import { useForm, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { useResetPasswordMutation, useValidateEmailTokenMutation } from "src/graphql/quark/quark.type"
import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import ROUTE_PATHS from "src/routes/route-path"

const validationSchema = yup.object({
  password: yup
    .string()
    .min(8, "Password should be of minimum 8 characters length")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(/[!@#$%^&*(),.?":{}|<>]/, "Password must contain at least one special character")
    .required("Password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), undefined], "Passwords must match")
    .required("Confirm Password is required")
})
const ResetPassword = () => {
  const [isValidatedLink, setIsValidatedLink] = useState<{ isValidated: boolean; error?: string } | null>(null)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const userId = searchParams.get("id")
  const token = searchParams.get("token")
  const { control, handleSubmit } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { password: "", confirmPassword: "" }
  })

  const [validateEmailToken, { loading: isValidatingEmailToken }] = useValidateEmailTokenMutation()
  useEffect(() => {
    const validateToken = async () => {
      try {
        const resp = await validateEmailToken({
          variables: {
            validateEmailTokenId: userId as string,
            token: token as string
          }
        })

        if (resp.data?.validateEmailToken) {
          setIsValidatedLink({ isValidated: true })
        } else {
          setIsValidatedLink({ isValidated: false, error: "Your Link is expired." })
        }
      } catch (err) {
        setIsValidatedLink({ isValidated: false, error: "Invalid Link." })
      }
    }

    if (userId && token) {
      validateToken()
    } else {
      setIsValidatedLink({ isValidated: false, error: "Invalid Link." })
    }
  }, [])

  const [resetPassword, { loading: isResetingPassword }] = useResetPasswordMutation()
  const onSubmit = async ({ password }: { password: string; confirmPassword: string }) => {
    const resp = await resetPassword({
      variables: {
        resetPasswordId: userId as string,
        token: token as string,
        password
      }
    })
    if (resp.data?.resetPassword) {
      toast.success("Password is Changed.")
      navigate(ROUTE_PATHS.LOGIN)
    }
  }

  if (isValidatedLink === null || isValidatingEmailToken) return <PageLoader isLoading />

  return (
    <AuthLayout>
      <CenteredBox sx={{ height: "100%" }}>
        {isValidatedLink?.isValidated ? (
          <>
            <Stack sx={{ width: "100%" }} spacing={2}>
              <Stack>
                <Typography variant="h3" textAlign={"center"}>
                  Change Your Password
                </Typography>
                <Typography variant="body1" textAlign={"center"} color="text.secondary" gutterBottom>
                  Secure your account with strong password
                </Typography>
              </Stack>
              <Controller
                name="password"
                control={control}
                render={({ field, fieldState }) => (
                  <OutlinedInput
                    {...field}
                    type="password"
                    label="Password"
                    placeholder="Password"
                    error={!!fieldState?.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field, fieldState }) => (
                  <OutlinedInput
                    {...field}
                    type="password"
                    label="Confirm Password"
                    placeholder="Confirm Password"
                    error={!!fieldState?.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Stack>

            <Stack mt={2} sx={{ width: "100%" }}>
              <Button variant="contained" fullWidth onClick={handleSubmit(onSubmit)}>
                {isResetingPassword ? <CircularProgress size="1rem" /> : "Change Password"}
              </Button>
            </Stack>
          </>
        ) : (
          <Typography>{isValidatedLink?.error}</Typography>
        )}
      </CenteredBox>
    </AuthLayout>
  )
}

export default ResetPassword
